import {
  getGetConversationQueryKey,
  getListConversationsQueryKey,
  useCreateConversation,
  useDeleteConversation,
  useGetConversation,
  useListConversations,
} from '@roko/api/generated/conversations/conversations'
import type { ConversationResponse } from '@roko/api/generated/models'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { streamConversationPrompt, streamConversationPromptResume } from '@/data/api/streamConversationPrompt'

/** List endpoint returns slim `{ id, title }` rows; detail/create return full ConversationResponse. */
export type TConversationListItem = Pick<ConversationResponse, 'id' | 'title'>

export type TSendChatPromptVariables = {
  conversationId: string
  data: { content: string }
  onChunk?: (chunk: string) => void
  onEmergency?: () => void
  onDangerCheck?: () => void
}

export type TResumeChatPromptVariables = {
  conversationId: string
  onChunk?: (chunk: string) => void
  onEmergency?: () => void
  onDangerCheck?: () => void
}

export const useChatsQuery = (enabled = true) =>
  useListConversations(undefined, {
    query: { enabled } as never,
  })

export const useChatQuery = (chatId?: string) => useGetConversation(chatId ?? '')

export const useCreateChatMutation = () => {
  const queryClient = useQueryClient()

  return useCreateConversation({
    mutation: {
      onSuccess: (conversation) => {
        queryClient.setQueryData<TConversationListItem[]>(getListConversationsQueryKey(), (current) => [
          { id: conversation.id, title: conversation.title },
          ...(current ?? []),
        ])
        queryClient.setQueryData(getGetConversationQueryKey(conversation.id), conversation)
      },
    },
  })
}

export const useDeleteChatMutation = () => {
  const queryClient = useQueryClient()

  return useDeleteConversation({
    mutation: {
      onSuccess: (_, { conversationId }) => {
        queryClient.setQueryData<TConversationListItem[]>(
          getListConversationsQueryKey(),
          (current) => current?.filter((conversation) => conversation.id !== conversationId) ?? []
        )
        queryClient.removeQueries({ queryKey: getGetConversationQueryKey(conversationId) })
      },
    },
  })
}

export const useSendChatPromptMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ conversationId, data, onChunk, onEmergency, onDangerCheck }: TSendChatPromptVariables) => {
      await streamConversationPrompt({
        conversationId,
        content: data.content,
        onChunk: onChunk ?? (() => undefined),
        onEmergency,
        onDangerCheck,
      })

      return { conversationId, content: data.content }
    },
    onSuccess: async ({ conversationId, content }) => {
      await queryClient.invalidateQueries({ queryKey: getGetConversationQueryKey(conversationId) })

      const trimmed = content.trim()
      if (trimmed) {
        queryClient.setQueryData<TConversationListItem[]>(getListConversationsQueryKey(), (current) => {
          if (!current) {
            return current
          }

          return current.map((item) => {
            if (item.id !== conversationId) {
              return item
            }

            if (!/^(Conversation-|Chat-|New chat)/.test(item.title)) {
              return item
            }

            return { ...item, title: trimmed }
          })
        })
      }

      void queryClient.invalidateQueries({ queryKey: getListConversationsQueryKey() })
    },
  })
}

export const useResumeChatPromptMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ conversationId, onChunk, onEmergency, onDangerCheck }: TResumeChatPromptVariables) => {
      await streamConversationPromptResume({
        conversationId,
        onChunk: onChunk ?? (() => undefined),
        onEmergency,
        onDangerCheck,
      })
      return { conversationId }
    },
    onSuccess: async ({ conversationId }) => {
      await queryClient.invalidateQueries({ queryKey: getGetConversationQueryKey(conversationId) })
    },
  })
}
